import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { AlertInfo } from 'src/app/models/alert-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CustomerService } from 'src/app/services/api/customer.service';

const PDF_MIME_TYPE = 'application/pdf';
const DEFAULT_FILE_NAME = 'nda.pdf';
const OBJECT_URL_LIFETIME_MS = 60000;

@Component({
  selector: 'customers-nda',
  imports: [CommonModule, NgbAlert],
  templateUrl: './customers-nda.component.html',
  styleUrl: './customers-nda.component.scss'
})
export class CustomersNdaComponent {
  @Input() customer: any;
  @Input() readonlyNda: boolean = false;

  isGenerating: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private customerService: CustomerService,
    private alertNotificationService: AlertNotificationService
  ) { }

  get canGenerate(): boolean {
    return !this.readonlyNda && !this.isGenerating && !!this.customer?.customerId;
  }

  generateNda(): void {
    const customerId = this.customer?.customerId;

    if (!customerId || this.isGenerating) {
      return;
    }

    this.alertInfo = { show: false, type: '', message: '' };
    this.isGenerating = true;

    this.customerService.createFileNDA(customerId, new FormData()).subscribe({
      next: (response) => {
        const body = response?.body;

        if (!body || body.size === 0) {
          this.alertNotificationService.error('The generated NDA file is empty');
          this.showAlert('warning', 'The generated NDA file is empty');
          this.isGenerating = false;
          return;
        }

        const pdfBlob = body.type === PDF_MIME_TYPE ? body : new Blob([body], { type: PDF_MIME_TYPE });
        const fileName = this.extractFileName(response.headers?.get('Content-Disposition'));

        this.openPdf(pdfBlob, fileName);
        this.alertNotificationService.success('NDA generated successfully');
        this.isGenerating = false;
      },
      error: (error) => {
        console.error('Error generating NDA:', error);
        this.alertNotificationService.error('Error generating the NDA');
        this.showAlert('danger', 'Error generating the NDA');
        this.isGenerating = false;
      }
    });
  }

  private openPdf(pdfBlob: Blob, fileName: string): void {
    const objectUrl = window.URL.createObjectURL(pdfBlob);
    const openedWindow = window.open(objectUrl, '_blank');

    if (!openedWindow) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.click();
    }
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS);
  }

  private extractFileName(contentDisposition: string | null | undefined): string {
    const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match?.[1] ? decodeURIComponent(match[1].trim()) : DEFAULT_FILE_NAME;
  }

  private showAlert(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
  }
}
